/* ==========================================================================
   Product: media gallery, variant selection, quantity offers, sticky bar.
   Variant data (prices formatted by Shopify, availability, quantity rules)
   is printed by Liquid in [data-product-json].
   ========================================================================== */

(() => {
  const theme = window.theme || (window.theme = {});
  const strings = () => theme.strings || {};

  /* Media gallery ---------------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('.gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
      this.thumbList = this.querySelector('.gallery__thumbs');
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('dialog.lightbox');
      this.current = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.thumbIndex, 10)));
      });
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.goTo(this.current - 1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.goTo(this.current + 1));
      this.track.addEventListener(
        'scroll',
        theme.debounce(() => this.syncFromScroll(), 70),
        { passive: true }
      );
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.current + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.current - 1);
        }
      });
      this.querySelectorAll('[data-zoom-index]').forEach((button) => {
        button.addEventListener('click', () => this.openZoom(parseInt(button.dataset.zoomIndex, 10), button));
      });

      const selected = this.dataset.selectedMedia;
      const selectedIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === selected);
      if (selectedIndex > 0) {
        window.requestAnimationFrame(() => this.goTo(selectedIndex, true));
      } else {
        this.setActive(0);
      }
    }

    offsetOf(index) {
      return this.slides[index].offsetLeft - this.slides[0].offsetLeft;
    }

    goTo(index, instant = false) {
      if (!this.slides.length) return;
      const target = Math.min(this.slides.length - 1, Math.max(0, index));
      this.track.scrollTo({
        left: this.offsetOf(target),
        behavior: instant || theme.prefersReducedMotion() ? 'auto' : 'smooth',
      });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    syncFromScroll() {
      const left = this.track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const delta = Math.abs(this.offsetOf(index) - left);
        if (delta < distance) {
          distance = delta;
          closest = index;
        }
      });
      if (closest !== this.current) this.setActive(closest);
    }

    setActive(index) {
      this.current = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        if (!active) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.thumbList && this.thumbs[index]) {
        const item = this.thumbs[index].parentElement;
        const left = item.offsetLeft - this.thumbList.clientWidth / 2 + item.clientWidth / 2;
        this.thumbList.scrollTo({ left, behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
      }
      if (this.counter) this.counter.textContent = String(index + 1);
      if (this.prevButton) this.prevButton.disabled = index <= 0;
      if (this.nextButton) this.nextButton.disabled = index >= this.slides.length - 1;
    }

    openZoom(index, opener) {
      if (!this.lightbox) return;
      theme.openDialog(this.lightbox, opener);
      const item = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
      if (item) {
        window.requestAnimationFrame(() => item.scrollIntoView({ block: 'start' }));
      }
    }
  }

  /* Product info ---------------------------------------------------------------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      try {
        this.data = JSON.parse(json.textContent);
      } catch (error) {
        console.error('[product] invalid variant data', error);
        return;
      }
      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      this.idInput = this.form ? this.form.querySelector('[data-variant-id]') : null;
      this.picker = this.querySelector('[data-variant-picker]');
      this.gallery = this.querySelector('media-gallery');
      this.bundles = this.querySelector('[data-bundles]');
      this.currentVariant =
        this.data.variants.find((variant) => this.idInput && String(variant.id) === this.idInput.value) ||
        this.data.variants[0];

      if (this.picker) {
        this.picker.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-input]')) this.onOptionChange();
        });
        this.updateAvailability(this.selectedOptions());
      }

      if (this.bundles) {
        this.bundles.addEventListener('change', (event) => {
          const radio = event.target.closest('.bundle__radio');
          if (radio) this.setQuantity(parseInt(radio.value, 10));
        });
      }

      const quantity = this.quantityInput();
      if (quantity) {
        quantity.addEventListener('change', () => this.syncBundles());
      }

      this.setupSticky();
    }

    disconnectedCallback() {
      if (this.onStickyScroll) {
        window.removeEventListener('scroll', this.onStickyScroll);
        window.removeEventListener('resize', this.onStickyScroll);
      }
      document.body.classList.remove('has-sticky-atc');
    }

    quantityInput() {
      return (
        document.querySelector(`input[name="quantity"][form="${this.formId}"]`) ||
        (this.form ? this.form.querySelector('input[name="quantity"]') : null)
      );
    }

    optionGroups() {
      return this.picker ? Array.from(this.picker.querySelectorAll('[data-option-index]')) : [];
    }

    selectedOptions() {
      return this.optionGroups().map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.data.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
    }

    onOptionChange() {
      const options = this.selectedOptions();
      this.optionGroups().forEach((group, i) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = options[i] || '';
      });
      this.updateAvailability(options);
      this.renderVariant(this.findVariant(options));
    }

    updateAvailability(options) {
      this.optionGroups().forEach((group, index) => {
        const isAvailable = (value) => {
          const match = this.data.variants.find(
            (variant) =>
              variant.options[index] === value && variant.options.every((v, j) => j === index || v === options[j])
          );
          return Boolean(match && match.available);
        };
        group.querySelectorAll('input[data-option-input]').forEach((input) => {
          const available = isAvailable(input.value);
          input.classList.toggle('is-unavailable', !available);
          const label = input.nextElementSibling;
          const hint = label ? label.querySelector('[data-unavailable-label]') : null;
          if (hint) hint.hidden = available;
        });
        group.querySelectorAll('select option').forEach((option) => {
          if (!option.dataset.label) option.dataset.label = option.value;
          const available = isAvailable(option.value);
          option.textContent = available ? option.dataset.label : `${option.dataset.label} — ${strings().unavailable}`;
        });
      });
    }

    addButtons() {
      const inside = Array.from(this.querySelectorAll('[data-add-to-cart]'));
      const linked = this.form ? Array.from(document.querySelectorAll(`[data-add-to-cart][form="${this.formId}"]`)) : [];
      return Array.from(new Set(inside.concat(linked, this.form ? Array.from(this.form.querySelectorAll('[data-add-to-cart]')) : [])));
    }

    setButtons(enabled, text) {
      this.addButtons().forEach((button) => {
        const label = button.querySelector('[data-button-label]');
        if (button.hasAttribute('aria-busy')) {
          button.dataset.wasDisabled = String(!enabled);
          button.dataset.label = text;
          return;
        }
        button.disabled = !enabled;
        if (label) label.textContent = text;
      });
      const buttonsWrap = this.form ? this.form.querySelector('[data-buttons]') : null;
      if (buttonsWrap) buttonsWrap.classList.toggle('is-unavailable', !enabled);
    }

    renderVariant(variant) {
      if (!variant) {
        if (this.idInput) this.idInput.disabled = true;
        this.setButtons(false, strings().unavailable);
        this.setStock(strings().variantUnavailable, 'out');
        return;
      }

      this.currentVariant = variant;
      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.disabled = !variant.available;
      }

      this.querySelectorAll('[data-installment-variant]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (this.dataset.updateUrl === 'true' && this.data.url) {
        window.history.replaceState(window.history.state, '', `${this.data.url}?variant=${variant.id}`);
      }

      this.querySelectorAll('[data-price]').forEach((price) => this.renderPrice(price, variant));
      this.querySelectorAll('[data-sticky-price]').forEach((price) => {
        price.innerHTML = variant.price;
      });

      this.setButtons(variant.available, variant.available ? strings().addToCart : strings().soldOut);
      this.setStock(variant.stock, variant.stockState);

      this.querySelectorAll('[data-sku-wrap]').forEach((wrap) => {
        wrap.hidden = !variant.sku;
        const sku = wrap.querySelector('[data-sku]');
        if (sku) sku.textContent = variant.sku || '';
      });

      this.applyQuantityRules(variant);
      this.renderBundles(variant);

      if (variant.media && this.gallery && typeof this.gallery.goToMedia === 'function') {
        this.gallery.goToMedia(variant.media);
      }

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    renderPrice(price, variant) {
      const onSale = Boolean(variant.compare);
      price.classList.toggle('price--on-sale', onSale);
      price.classList.toggle('price--sold-out', !variant.available);
      const compareWrap = price.querySelector('[data-price-compare-wrap]');
      const compare = price.querySelector('[data-price-compare]');
      const current = price.querySelector('[data-price-current]');
      const label = price.querySelector('[data-price-label]');
      const badge = price.querySelector('[data-price-badge]');
      const soldOut = price.querySelector('[data-price-sold-out]');
      const unit = price.querySelector('[data-price-unit]');
      if (compareWrap) compareWrap.hidden = !onSale;
      if (compare) compare.innerHTML = variant.compare || '';
      if (current) current.innerHTML = variant.price;
      if (label) label.textContent = onSale ? strings().salePrice : strings().regularPrice;
      if (badge) {
        badge.hidden = !(onSale && variant.available);
        badge.textContent = variant.badge || '';
      }
      if (soldOut) soldOut.hidden = variant.available;
      if (unit) {
        unit.hidden = !variant.unit;
        unit.innerHTML = variant.unit || '';
      }
    }

    setStock(text, state) {
      this.querySelectorAll('[data-stock]').forEach((stock) => {
        stock.dataset.state = state;
        const label = stock.querySelector('[data-stock-text]');
        if (label) label.textContent = text;
      });
    }

    applyQuantityRules(variant) {
      const input = this.quantityInput();
      if (!input) return;
      input.min = variant.qmin;
      input.dataset.min = variant.qmin;
      input.step = variant.qstep;
      if (variant.qmax) {
        input.max = variant.qmax;
      } else {
        input.removeAttribute('max');
      }
      let value = parseInt(input.value, 10) || variant.qmin;
      if (value < variant.qmin) value = variant.qmin;
      if (variant.qmax && value > variant.qmax) value = variant.qmax;
      if (variant.qstep > 1 && value % variant.qstep !== 0) value = Math.ceil(value / variant.qstep) * variant.qstep;
      input.value = value;

      const rules = this.querySelector('[data-quantity-rules]');
      if (rules) {
        const parts = [];
        if (variant.qstep > 1) parts.push(theme.format(strings().quantityStep, { quantity: variant.qstep }));
        if (variant.qmin > 1) parts.push(theme.format(strings().quantityMin, { quantity: variant.qmin }));
        if (variant.qmax) parts.push(theme.format(strings().quantityMax, { quantity: variant.qmax }));
        rules.innerHTML = '';
        parts.forEach((part) => {
          const span = document.createElement('span');
          span.textContent = part;
          rules.appendChild(span);
        });
      }
      const wrapper = input.closest('quantity-input');
      if (wrapper && typeof wrapper.updateButtons === 'function') wrapper.updateButtons();
      this.syncBundles();
    }

    setQuantity(quantity) {
      const input = this.quantityInput();
      if (!input || Number.isNaN(quantity)) return;
      input.value = quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncBundles();
    }

    syncBundles() {
      if (!this.bundles) return;
      const input = this.quantityInput();
      const quantity = input ? parseInt(input.value, 10) : 1;
      this.bundles.querySelectorAll('.bundle').forEach((bundle) => {
        const radio = bundle.querySelector('.bundle__radio');
        const selected = parseInt(radio.value, 10) === quantity;
        radio.checked = selected;
        bundle.classList.toggle('is-selected', selected);
      });
    }

    renderBundles(variant) {
      if (!this.bundles || !Array.isArray(variant.bundles)) return;
      this.bundles.querySelectorAll('.bundle').forEach((bundle) => {
        const radio = bundle.querySelector('.bundle__radio');
        const data = variant.bundles[parseInt(radio.dataset.bundleIndex, 10)];
        if (!data) return;
        const price = bundle.querySelector('[data-bundle-price]');
        const compare = bundle.querySelector('[data-bundle-compare]');
        if (price) price.innerHTML = data.price;
        if (compare) {
          compare.hidden = !data.compare;
          compare.innerHTML = data.compare || '';
        }
      });
    }

    setupSticky() {
      this.sticky = this.querySelector('[data-sticky-atc]');
      this.stickyTarget = this.querySelector('.product__buy');
      if (!this.sticky || !this.stickyTarget) return;
      this.stickyButton = this.sticky.querySelector('[data-add-to-cart]');
      this.stickyVisible = false;
      let frame = null;
      this.onStickyScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          this.updateSticky();
        });
      };
      window.addEventListener('scroll', this.onStickyScroll, { passive: true });
      window.addEventListener('resize', this.onStickyScroll, { passive: true });
      this.updateSticky();
    }

    updateSticky() {
      const show = this.stickyTarget.getBoundingClientRect().bottom < 0;
      if (show === this.stickyVisible) return;
      this.stickyVisible = show;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', String(!show));
      if (this.stickyButton) this.stickyButton.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('has-sticky-atc', show);
    }
  }

  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
})();
